/* eslint-disable @typescript-eslint/camelcase */
import React, { useMemo, useState, useEffect, ReactNode, ReactElement } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { PracticeSheet, BackingTracksData } from '@fenderdigital/tablature';
import PracticeModeComponent from '@fenderdigital/notation/feature/practice-mode';
import Spinner from '@fenderdigital/ui/Spinner';
import { TrackFile } from '@fenderdigital/tablature/audio/TrackPlayer';
import isDomUsable from '@fenderdigital/utils/dom';
import usePlayUser from '@fenderdigital/custom-hooks/hooks/usePlayUser';
import {
  Collection,
  Lesson,
  Path,
  Playlist,
  LessonType,
} from '@fenderdigital/api-definitions/play';
import { FenderNotation } from '@fenderdigital/notation/types';
import WebAnalyticsAdapter from '@fenderdigital/notation/analytics/web-adapter';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import { RootState } from '../../../redux-types/root.redux-types';
import useDebounce from '../../../utils/useDebounce';
import '../../../components/Tablature/tab.scss';
import { getTabLevelSettings } from '../../../lib/tab-data';
import segment from '../../../core/segment';
import UserAgent from '../../../lib/user-agent';
import { findLessonInFlatLessons } from '../../lesson/components/lesson-utils';
import BackButton, { BackButtonWrapper } from '../../../components/BackButton/BackButton';
import EllipsisDropdown from '../../../components/EllipsisDropdown';
import MarkComplete from '../../../components/MarkComplete';
import useDataLoader from '../../../utils/useDataLoader';
import {
  EventNames,
  trackPageEvents,
  trackEvent,
  trackEventAndUpdate,
  getAlgoliaTracking,
} from '../../../lib/analytics';
import { slugPageTitle } from '../../../utils/string-utils';
import HeadTitle from '../../../components/Head/HeadTitle';
import SegmentAdapter from './SegmentAdapter';
import useQueueStreaksModal from '../../../hooks/useQueueStreaksModal';
import {
  getActivityProperties,
  getCurriculumProperties,
  getNextActivityProperties,
  getUserActivitiesCompleted,
  isCourseCompleted,
} from '../../../utils/activity-utils';
import { getFlag } from '../../../lib/feature-flags';
import { isLocked } from '../../../utils/access-utils';
import useBackHandler from '../../../components/BackButton/useBackHandler';
import * as FavoritesTypes from '../../favorites/types';
import { FavoriteDataProps } from '../../favorites/Favorites';

interface ArtistAndTitle {
  title: string;
  artist: string;
}

interface Practice extends Omit<Lesson, 'backing-tracks'> {
  'backing-tracks': TrackFile[];
  'backing-tracks-count-in': number;
}

interface MyPath extends Path {
  inProgress: boolean;
  flatLessons?: Lesson[];
  playlists: Playlist[];
}

interface ConditionalProps extends Path {
  tabXML: string;
  worker: any;
}

const PracticeSheetContainer = styled.div`
  height: 100%;
  // Overwrite padding from <SubscribedLayoutContent>
  padding-bottom: 0 !important;
`;

interface PracticeSheetViewProps {
  collectionSlug: string | null;
  collection: Collection | null;
  tabXML: string;
  history: RouteComponentProps['history'];
  currentPlaylist: any;
  currentPractice: Practice;
  currentPracticeCompleted: boolean;
  courseSlug: string;
  error: string | null;
  favoritesKeys: FavoritesTypes.FavoritesKeys;
  favoriteData?: FavoriteDataProps;
  nextLesson: Lesson | null;
  pathLessons: Lesson[];
  practiceSlug: string;
  upNextTitle: string | undefined;
  myPath: MyPath;
  level?: string;
  pathID: string;
  nextURL: string;
  updateCourseProgress: (
    courseID: string,
    lessonID: string,
    courseType: string,
    lessonType: string,
    instrument?: string,
  ) => void;
  updateLessonCompletedProgress: (completeData: object[]) => void;
  updateLessonProgress: (
    lessonID: string,
    lessonType: LessonType,
    videoWatchedSeconds: 25 | 50,
    instrument: string,
    videoTotalSeconds: 100,
  ) => void;
  updatePathProgress: (
    pathID: string,
    playlistID: string,
    courseID: string,
    lessonID: string,
    style: string,
    instrument: string,
    courseType: string,
    lessonType: string,
  ) => void;
  getLessonsProgress: (lessonIds: string[]) => object;
  setFavorite: (
    favoritesId: string,
    activityType: string,
    sourcePath: string,
    favoriteData?: FavoriteDataProps,
    userID?: string,
  ) => void;
  unsetFavorite: (
    favoritesId: string,
    activityType: string,
    sourcePath: string,
    favoriteData?: FavoriteDataProps,
    userID?: string,
  ) => void;
}

const PracticeSheetView: React.FC<PracticeSheetViewProps> = ({
  collectionSlug,
  collection,
  currentPractice,
  favoritesKeys,
  favoriteData = {},
  updateCourseProgress,
  updateLessonCompletedProgress,
  updateLessonProgress,
  updatePathProgress,
  setFavorite,
  unsetFavorite,
  myPath,
  currentPracticeCompleted,
  nextLesson,
  nextURL,
  history,
  upNextTitle,
  practiceSlug,
  currentPracticeCompleted: isComplete,
  currentPlaylist,
  pathLessons,
}) => {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const selector = (state: RootState) => ({
    myProgress: state.myProgress,
    paths: state.paths.watching,
    currentLessonOnPath: state.paths.watching.currentLesson,
    currentCourseOnPath: state.paths.watching.currentCourse,
    currentCourse: state.courses.watching.currentCourse,
    currentLesson: state.courses.watching.currentLesson,
  });
  const {
    paths,
    myProgress,
    currentLessonOnPath,
    currentCourseOnPath,
    currentLesson,
    currentCourse,
  } = useSelector(selector);
  const [courseCompleted, setCourseCompleted] = useState(false);
  const [nextActivityProperties, setNextActivityProperties] = useState({});
  const [activityProperties, setActivityProperties] = useState({});
  const [curriculumProperties, setCurriculumProperties] = useState({});
  const [userActivitiesCompleted, setUserActivitiesCompleted] = useState({});
  const [activityRepeated, setActivityRepeated] = useState(0);
  const [activityCompleted, setActivityCompleted] = useState(false);
  const [activityStarted, setActivityStarted] = useState(false);
  const { accessLevel: userAccessLevel, user } = usePlayUser();
  const backHandler = useBackHandler();
  const isPlay5 = getFlag('isPlay5');
  const isPracticeModeV2Enabled = getFlag('isPracticeModeV2Enabled');
  const currentPracticeAccessLevel = currentPractice?.['access-levels'];
  const isAvailable = !(isPlay5 && isLocked(currentPracticeAccessLevel, userAccessLevel));
  const slugTitle = slugPageTitle(practiceSlug);
  const pageTitle = `Fender Play Online Guitar Lessons - Learn How to Play Guitar - ${slugTitle}`;
  const isMobile = useMemo(() => UserAgent.isMobileOrTabletUAString(), []);
  const segmentAdapter: SegmentAdapter = useMemo(
    () =>
      new SegmentAdapter(segment, {
        fc_id: user?.userID,
        practice_sheet_id: currentPractice?.id,
        practice_sheet_slug: practiceSlug,
        practice_sheet_title: currentPractice?.title,
      }),
    [currentPractice, practiceSlug],
  );
  const globalProperties = {
    fc_id: user?.userID,
    user_id: user?.userID,
  };
  const activityType = 'practice-mode';
  const isOnPath = myPath?.inProgress;
  const trackingActivityProperties = {
    ...globalProperties,
    ...activityProperties,
    activity_type: activityType,
  };
  const getAdjustedWidth = (): number => {
    return isDomUsable() ? window.innerWidth : 0;
  };

  const [width, setWidth] = useState(getAdjustedWidth());
  const debouncedWidth = useDebounce(width, 300);

  const widthListener = (): void => setWidth(getAdjustedWidth());

  const {
    isFetching,
    conditionalProps,
  }: { isFetching: boolean; conditionalProps: any } = useDataLoader();
  const { tabXML, worker } = conditionalProps as ConditionalProps;

  useQueueStreaksModal();

  useEffect(() => {
    if (!isMobile) window.addEventListener('resize', widthListener, { passive: true });
    return (): void => {
      window.removeEventListener('resize', widthListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    user &&
      currentPractice &&
      trackPageEvents(EventNames.PRACTICE_MODE, {
        fc_id: user?.userID,
        practice_sheet_id: currentPractice.id,
        practice_sheet_slug: practiceSlug,
        practice_sheet_title: currentPractice.title,
        ...getAlgoliaTracking('view', currentPractice.id),
      });
  }, [currentPractice, practiceSlug, user]);

  useEffect(() => {
    if (!isFetching && currentPractice) {
      const { id, instrument, type } = currentPractice;
      const { flatLessons, id: pathID, style } = myPath;
      const foundFlatLesson = findLessonInFlatLessons(currentPractice, flatLessons);
      if (foundFlatLesson) {
        const { playlistID, courseID, courseType } = foundFlatLesson;
        updatePathProgress(pathID, playlistID, courseID, id, style, instrument, courseType, type);
      } else if (currentCourse && currentLesson) {
        const { id, instrument, type } = currentLesson;
        const { id: courseID, type: courseType } = currentCourse;
        updateCourseProgress(courseID, id, courseType, type, instrument);
      }
    }
  }, [
    currentCourse,
    currentLesson,
    currentPractice,
    myPath,
    updateCourseProgress,
    updatePathProgress,
    isFetching,
  ]);

  useEffect(() => {
    user &&
      activityStarted &&
      trackEventAndUpdate(
        user?.userID,
        EventNames.ACTIVITY_STARTED,
        {
          ...trackingActivityProperties,
          repeat: activityRepeated,
          ...getAlgoliaTracking('click', currentPractice?.id),
        },
        curriculumProperties,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityRepeated, activityStarted]);

  useEffect(() => {
    if (!isFetching) {
      const currentPathStyle = myPath.style;
      const courseProgressStatus = isCourseCompleted(myProgress, myPath);
      const activityPropertiesEvent = getActivityProperties(currentLessonOnPath, currentPathStyle);
      const curriculumPropertiesEvent = getCurriculumProperties(paths);
      const userActivitiesCompletedEvent = getUserActivitiesCompleted(myProgress, myPath);
      const nextActivityEvent = getNextActivityProperties(nextLesson);
      setActivityProperties(activityPropertiesEvent);
      setCurriculumProperties(curriculumPropertiesEvent);
      setUserActivitiesCompleted(userActivitiesCompletedEvent);
      setCourseCompleted(courseProgressStatus);
      setNextActivityProperties(nextActivityEvent);
    }
  }, [isFetching, activityRepeated, myPath, myProgress, currentLessonOnPath, paths, nextLesson]);

  const getSegmentUpNextData = (): object => {
    const { slug, id } = currentPractice || { slug: '', id: '' };
    const {
      slug: nextSlug,
      id: nextID,
      title: nextTitle,
      'activity-type': nextActivityType,
    } = nextLesson || { slug: null, id: null, title: null, 'activity-type': null };

    /* eslint-disable @typescript-eslint/camelcase */
    return {
      practice_sheeet_id: id,
      practice_sheet_slug: slug,
      next_activity_id: nextID,
      next_activity_slug: nextSlug,
      next_activity_title: nextTitle,
      next_activity_type: nextActivityType,
    };
  };

  const onPlay = (): void => {
    if (currentPracticeCompleted && activityCompleted) {
      setActivityRepeated(activityRepeated + 1);
    }
    setActivityStarted(true);
  };

  const onNext = (): void => {
    if (!history) return;
    history.push(nextURL);
    trackEvent(EventNames.PRACTICE_MODE_NEXT_LESSON_CLICKED, {
      fc_id: user?.userID,
      ...getSegmentUpNextData(),
    });
    if (!activityCompleted) {
      trackEvent(EventNames.ACTIVITY_DISMISSED, {
        ...trackingActivityProperties,
      });
    }
  };

  const trackActivityCompleted = (): void => {
    trackEventAndUpdate(
      user?.userID,
      EventNames.ACTIVITY_COMPLETED,
      {
        ...trackingActivityProperties,
        ...nextActivityProperties,
        is_on_path: isOnPath,
        ...getAlgoliaTracking('conversion', currentPractice?.id),
      },
      userActivitiesCompleted,
    );
  };

  const onProgressMilestone = (progress: number): void => {
    if (!currentPractice) return;
    const { id, slug, title, level, type } = currentPractice;
    const { instrument } = myPath;

    const shouldTrackCollections = !!collection && collectionSlug === collection.slug;
    const collectionSegmentData = !shouldTrackCollections
      ? {}
      : {
          collection_id: collection?.id ?? '',
          collection_name: collection?.title ?? '',
        };

    if (progress === 100 && courseCompleted) {
      trackEventAndUpdate(
        user?.userID,
        EventNames.COURSE_COMPLETED,
        {
          ...globalProperties,
          course_id: currentCourseOnPath.id,
          course_slug: currentCourseOnPath.slug,
          course_name: currentCourseOnPath.title,
          ...getAlgoliaTracking('conversion', currentCourseOnPath.id),
        },
        userActivitiesCompleted,
      );
    }
    /* eslint-disable @typescript-eslint/camelcase */
    const segmentData = {
      fc_id: user?.userID,
      practice_sheet_id: id,
      practice_sheet_title: title,
      practice_sheet_slug: slug,
      tablature_pick_level: level ? +level : 1,
      lesson_type: type,
      checkpoint_percent: progress,
      ...collectionSegmentData,
    };
    trackEvent(EventNames.PRACTICE_MODE_PLAYBACK_CHECKPOINT_REACHED, segmentData);
    if ((progress === 50 || progress === 25) && !currentPracticeCompleted) {
      updateLessonProgress(id, type, progress, instrument, 100);
    }

    switch (type) {
      case 'song':
      case 'riff':
        progress === 50 && trackActivityCompleted();
        break;
      case 'basics':
      case 'chord':
      case 'exercise':
      case 'skill':
      case 'technique':
      case 'theory':
      case 'tone':
        progress === 25 && trackActivityCompleted();
        break;
      default:
        break;
    }
  };

  const handleComplete = (completed = true): void => {
    const { id, type, 'activity-type': activityType, instrument } = currentPractice || {
      id: '',
      type: '',
      'activity-type': '',
      instrument: '',
    };
    const markLessonCompleteData = {
      'lesson-id': id,
      'lesson-type': type,
      'activity-type': activityType || 'practice-sheet',
      instrument,
      completed,
    };
    updateLessonCompletedProgress([markLessonCompleteData]);

    setActivityCompleted(true);
  };

  const getArtistAndTitle = (): ArtistAndTitle => {
    if (!currentPractice) return { title: '', artist: '' };
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore // TODO
    const { title, artists } = currentPractice;
    if (!artists || !Array.isArray(artists) || artists.length === 0) {
      return { title, artist: '' };
    }
    const [firstArtist] = artists;
    const { name: artist = '' } = firstArtist;
    return { title, artist };
  };

  const getBackingTracks = (): BackingTracksData | undefined => {
    if (!currentPractice?.['backing-tracks']) return;
    // eslint-disable-next-line consistent-return
    return {
      'backing-tracks': currentPractice['backing-tracks'],
      'backing-tracks-count-in': currentPractice['backing-tracks-count-in'] || 8,
    };
  };

  const renderDropdownMenu = (): ReactElement | null => {
    const { id: lessonId, 'activity-lesson-video': activityLessonVideo } = currentPractice;
    const segmentData = {
      lesson_id: lessonId,
      video_id: activityLessonVideo?.id,
      is_on_path: pathLessons?.some((lesson: Lesson): boolean => lessonId === lesson.id),
      user_id: user?.userID,
      path_id: myPath?.id,
      play_list_id: currentPlaylist?.id,
      genre: myPath?.style,
    };

    const onMarkedComplete = (): void => {
      segment.event(`lesson_mark_as_complete${isComplete ? '_undo' : ''}`, segmentData);
      handleComplete(!isComplete);
    };

    if (!isAvailable) {
      return <Redirect to="/plans/upgrade" />;
    }

    return (
      <div className="pt3 flex">
        <EllipsisDropdown
          favoritesId={lessonId}
          dataId="practice-ellipsis"
          activityType="practice-sheet"
          sourcePath="practice-sheet-detail"
        >
          <MarkComplete isComplete={isComplete} isCourse={false} onConfirm={onMarkedComplete} />
        </EllipsisDropdown>
      </div>
    );
  };

  const renderPracticeModeV1 = (): ReactNode => {
    if (!currentPractice) return null;
    const { artist, title } = getArtistAndTitle();
    const { level, instrument, id, slug } = currentPractice;
    const backingtracks = getBackingTracks();
    const settings = getTabLevelSettings(level, instrument);
    segmentAdapter.metadata = { id, title, slug, artist, hasBackingTracks: !!backingtracks };
    return (
      <PracticeSheet
        artist={artist}
        backingTracks={backingtracks}
        title={title}
        tabXML={tabXML}
        settings={settings}
        worker={worker}
        containerWidth={debouncedWidth}
        containerHeight="calc(100vh - 60px)"
        segmentAdapter={segmentAdapter}
        upNextTitle={upNextTitle}
        onPlay={onPlay}
        onUpNext={onNext}
        onProgressMilestone={onProgressMilestone}
        backButton={
          <>
            {/*
              When `isMobile` is true, it'll add top spacing.
              This is to prevent the BackButton from getting cut-off on mobile and tablet.
            */}
            <BackButtonWrapper isMobile={isMobile}>
              <BackButton
                onClick={(): void => {
                  trackEvent(EventNames.PRACTICE_MODE_EXIT, {
                    fc_id: user?.userID,
                    practice_sheet_id: currentPractice.id,
                    practice_sheet_slug: practiceSlug,
                    practice_sheet_title: currentPractice.title,
                  });
                  trackEvent(EventNames.ACTIVITY_DISMISSED, {
                    ...trackingActivityProperties,
                  });
                }}
              />
            </BackButtonWrapper>
          </>
        }
        dropdown={renderDropdownMenu()}
        onComplete={handleComplete}
        sampleBaseUrl={process.env.REACT_APP_CLIENT_FENDER_COM}
        cowbell={practiceSlug === 'ps-dont-fear-the-reaper-blue-oyster-cult'}
        traxUI
      />
    );
  };

  const renderPracticeModeV2 = (): ReactNode => {
    if (!currentPractice) return null;
    const { artist, title } = getArtistAndTitle();
    const backingtracks = getBackingTracks();
    const isFavorited = currentPractice.id in favoritesKeys;
    const { id, slug, title: practiceTitle } = currentPractice;
    const hasBackingTracks = currentPractice?.['backing-tracks'].length > 0;
    const analyticsAdapter = new WebAnalyticsAdapter(trackEvent);
    analyticsAdapter.practiceMetadata = {
      id,
      slug,
      title: practiceTitle,
      hasBackingTracks,
    };

    const handleUIEvents = (event: FenderNotation.UIEvent): void => {
      if (event.name === 'BackButtonClick') {
        trackEvent(EventNames.PRACTICE_MODE_EXIT, {
          fc_id: user?.userID,
          practice_sheet_id: currentPractice.id,
          practice_sheet_slug: practiceSlug,
          practice_sheet_title: currentPractice.title,
        });
        trackEvent(EventNames.ACTIVITY_DISMISSED, {
          ...trackingActivityProperties,
        });
        backHandler();
      }

      if (event.name === 'MarkCompleteToggle') {
        const { id: lessonId, 'activity-lesson-video': activityLessonVideo } = currentPractice;
        const segmentData = {
          lesson_id: lessonId,
          video_id: activityLessonVideo?.id,
          is_on_path: pathLessons?.some((lesson: Lesson): boolean => lessonId === lesson.id),
          user_id: user?.userID,
          path_id: myPath?.id,
          play_list_id: currentPlaylist?.id,
          genre: myPath?.style,
        };
        segment.event(`lesson_mark_as_complete${isComplete ? '_undo' : ''}`, segmentData);
        handleComplete(!isComplete);
      }

      if (event.name === 'FavoriteToggle') {
        const sourcePath = 'practice-sheet-detail';
        const favoriteActivity = 'practice-sheet';

        if (currentPractice.id in favoritesKeys) {
          unsetFavorite(
            currentPractice.id,
            favoriteActivity,
            sourcePath,
            favoriteData,
            user?.userID,
          );
        } else {
          setFavorite(currentPractice.id, favoriteActivity, sourcePath, favoriteData, user?.userID);
        }
      }
    };

    return (
      <PracticeModeComponent
        tabData={tabXML}
        title={title}
        artist={artist}
        showTitleAndArtist
        analyticsAdapter={analyticsAdapter}
        isFullScreen
        hideFullScreenButton
        isZoomEnabled
        hideZoomButton
        worker={worker}
        tracksData={backingtracks}
        onComplete={handleComplete}
        onPlay={onPlay}
        onProgressMilestone={onProgressMilestone}
        upNextTitle={upNextTitle}
        onUpNext={onNext}
        showBackButton
        showFavoriteButton
        showMarkCompleteDropdown
        onUIEvent={handleUIEvents}
        isMarkedComplete={isComplete}
        isFavorited={isFavorited}
      />
    );
  };

  if (isFetching)
    return (
      <div>
        <Spinner fullPage />
      </div>
    );
  return (
    <PracticeSheetContainer className="practice-sheet-view">
      <HeadTitle title={pageTitle} />
      {isPracticeModeV2Enabled ? renderPracticeModeV2() : renderPracticeModeV1()}
    </PracticeSheetContainer>
  );
};

export default PracticeSheetView;
